import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { match3API } from './match3Api';
import useMatch3Playback from './useMatch3Playback';
import { swapMakesMatch, thresholdsOf } from './boardModel';
import { HINT_IDLE_MS, TIMING } from './constants';
import { selection } from '../common/haptics';
import Match3Board from './components/Match3Board';
import { TopBar, ScorePanel, GemBonusBar } from './components/Match3Hud';
import Match3EndScreen from './components/Match3EndScreen';
import './match3.css';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { tr } from '../../../i18n/msg';
import { missionName } from '../../lair/utils/lairAssets';

/**
 * MATCH3 — Gem Mine mission mini-game (7×9 portrait board, 20 moves).
 * Route: /lair/match3/:runId. Server-authoritative: the client only sends swaps {x1,y1,x2,y2},
 * plays back the returned events and then applies the state. Contract: match3Api.js / Match3StateDTO.
 */
export default function Match3Page() {
  const { t } = useTranslation();
  const { runId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // 'notfound' | 'error'
  const [state, setState] = useState(null);
  const [run, setRun] = useState(null);
  const [expired, setExpired] = useState(false);
  const [selected, setSelected] = useState(null);
  const [hint, setHint] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [hero, setHero] = useState(null);
  const [cheering, setCheering] = useState(0);

  const playback = useMatch3Playback();
  const { reset: resetBoard } = playback;
  const stateRef = useRef(null);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const toastTimer = useRef(null);
  const runRetryRef = useRef(false);
  const [idleKey, setIdleKey] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(toastTimer.current);
    };
  }, []);

  const showToast = useCallback((text) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2200);
  }, []);

  const applyResponse = useCallback((data) => {
    const next = data?.state ?? null;
    if (next) {
      stateRef.current = next;
      setState(next);
      resetBoard(next.board);
    } else {
      setExpired(true);
    }
    if (data?.run !== undefined) setRun(data.run || null);
  }, [resetBoard]);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await match3API.getGame(runId);
      if (!mountedRef.current) return;
      applyResponse(data);
      if ((data?.events || []).some((ev) => ev?.e === 'timeout')) setExpired(true);
      setLoadError(null);
    } catch (e) {
      if (!mountedRef.current) return;
      const status = e?.response?.status;
      if (!silent || !stateRef.current) setLoadError(status === 404 ? 'notfound' : 'error');
    } finally {
      if (mountedRef.current && !silent) setLoading(false);
    }
  }, [runId, applyResponse]);

  useEffect(() => { load(); }, [load]);

  // hero miniature (optional — the game works without it)
  useEffect(() => {
    let alive = true;
    match3API.getHero(runId).then((h) => { if (alive) setHero(h || null); }).catch(() => {});
    return () => { alive = false; };
  }, [runId]);

  // hero cheers on big combos for a moment
  useEffect(() => {
    if (!playback.cheer) return undefined;
    setCheering(playback.cheer);
    const t = setTimeout(() => setCheering(0), 1300);
    return () => clearTimeout(t);
  }, [playback.cheer]);

  const finished = !!state?.result || expired;
  const interactive = !!state && !finished && !busy && !playback.playing;

  // game over but the reward DTO hasn't arrived yet → re-read once
  useEffect(() => {
    if (!finished || run || runRetryRef.current || playback.playing) return undefined;
    runRetryRef.current = true;
    const t = setTimeout(() => load({ silent: true }), 1500);
    return () => clearTimeout(t);
  }, [finished, run, playback.playing, load]);

  // hint after HINT_IDLE_MS without input
  useEffect(() => {
    if (!interactive || hint) return undefined;
    const t = setTimeout(async () => {
      try {
        const data = await match3API.getGame(runId, { hint: true });
        if (!mountedRef.current || busyRef.current) return;
        const h = data?.state?.hint;
        if (Array.isArray(h) && h.length === 2) setHint(h);
      } catch (e) { /* hints are optional */ }
    }, HINT_IDLE_MS);
    return () => clearTimeout(t);
  }, [interactive, hint, idleKey, runId]);

  const goBack = useCallback(() => navigate('/lair'), [navigate]);

  const swap = useCallback(async (a, b) => {
    if (busyRef.current) return;
    setSelected(null);
    setHint(null);
    setIdleKey((k) => k + 1);
    // checked locally first: an invalid swap just bounces back — no request, no 422
    if (!swapMakesMatch(stateRef.current?.board, a, b)) {
      playback.invalid(a, b);
      return;
    }
    busyRef.current = true;
    setBusy(true);
    // the gems move right away; the server round trip runs behind the animation
    playback.optimisticSwap(a, b);
    const swapShown = new Promise((r) => setTimeout(r, TIMING.swap));
    try {
      let data;
      try {
        data = await match3API.swap(runId, a, b);
      } catch (e) {
        const status = e?.response?.status;
        await swapShown;
        if (!mountedRef.current) return;
        playback.revertSwap(a, b);
        if (status === 422) {
          // rare (client and server disagree / game over) — a soft hint and a resync
          showToast(i18n.t('match3.no_match', 'No match there'));
          await load({ silent: true });
        } else if (status === 404) {
          showToast(i18n.t('game.run_not_found', 'Run not found'));
        } else {
          showToast(i18n.t('game.connection_problem', 'Connection problem — try again'));
          await load({ silent: true });
        }
        return;
      }
      if (!mountedRef.current) return;
      if ((data?.events || []).some((ev) => ev?.e === 'timeout')) setExpired(true);
      const before = stateRef.current;
      await swapShown;
      await playback.play(data?.events || [], { score: before?.score ?? 0, movesLeft: before?.movesLeft ?? null },
        () => applyResponse(data), { swapDone: true });
    } finally {
      busyRef.current = false;
      if (mountedRef.current) setBusy(false);
    }
  }, [runId, playback, showToast, load, applyResponse]);

  const onSelect = useCallback((cell) => {
    if (cell) selection();
    setSelected(cell);
    setHint(null);
    setIdleKey((k) => k + 1);
  }, []);

  // ── render ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="m3-page m3-page--center">
        <div className="m3-spinner" aria-label={t('common.loading', 'Loading…')} />
        <div className="m3-muted">{t('match3.opening', 'Opening the gem mine…')}</div>
      </div>
    );
  }

  if (loadError || (!state && !run && !expired)) {
    const notFound = loadError === 'notfound';
    return (
      <div className="m3-page m3-page--center">
        <div className="m3-error-title">{notFound ? t('game.run_not_found', 'Run not found') : t('match3.load_failed', "Couldn't load the mine")}</div>
        <div className="m3-muted">{notFound ? t('game.no_longer_exists', 'This game no longer exists.') : t('game.check_connection', 'Check your connection and try again.')}</div>
        <div className="m3-error-btns">
          {!notFound && <button type="button" className="app-btn app-btn--blue" onClick={() => load()}>{t('common.retry', 'Retry')}</button>}
          <button type="button" className="app-btn app-btn--green" onClick={goBack}>{t('mission.back_to_lair', 'Back to Lair')}</button>
        </div>
      </div>
    );
  }

  const thresholds = thresholdsOf(state);
  const score = playback.score ?? state?.score ?? 0;
  const movesLeft = playback.movesLeft ?? state?.movesLeft;
  const banner = playback.banner;
  const showEnd = finished && !playback.playing;

  return (
    <div className="m3-page">
      <TopBar title={missionName(run, t)} movesLeft={movesLeft} movesTotal={state?.movesTotal} onBack={goBack} />
      <ScorePanel score={score} thresholds={thresholds} hero={hero} cheer={cheering} />

      <div className="m3-field">
        {state ? (
          <Match3Board
            width={state.width || 7}
            height={state.height || 9}
            pieces={playback.pieces}
            effects={playback.effects}
            selected={interactive ? selected : null}
            hint={interactive ? hint : null}
            disabled={!interactive}
            onSelect={onSelect}
            onSwap={swap}
          />
        ) : (
          <div className="m3-field__empty" />
        )}
        {banner && (
          <div
            key={banner.n}
            className={`m3-banner${banner.combo >= 3 ? ' m3-banner--big' : ''}${banner.end ? ` m3-banner--${banner.end}` : ''}`}
            data-testid="banner"
          >
            {tr(t, banner.text)}
          </div>
        )}
        {toast && <div className="m3-toast" role="status">{toast}</div>}
      </div>

      <GemBonusBar gemBonus={state?.gemBonus} />

      {showEnd && (
        <Match3EndScreen
          result={state?.result || 'LOSE'}
          stars={state?.stars ?? run?.stars ?? 0}
          score={state?.score ?? 0}
          target={thresholds[0]}
          run={run}
          timedOut={expired && state?.result !== 'WIN'}
          onBack={goBack}
        />
      )}
    </div>
  );
}
